import { ITINERARY_PANEL } from '@web-platform/shared-banana-constants/src/routes';
import { matchHashPath } from '@web-platform/shared-banana-utils-frontend/src/helpers/removeHashSymbol';
import adverts, * as fromAdverts from '@web-platform/shared-universal-redux/src/state/shared/adverts';
import * as fromFlightBookingPanel from '@web-platform/shared-universal-redux/src/state/shared/flightBookingPanel';
import flightSearchResults, * as fromFlightSearchResults from '@web-platform/shared-universal-redux/src/state/shared/flightSearchResults';
import * as fromFps from '@web-platform/shared-universal-redux/src/state/shared/fps';
import network from '@web-platform/shared-universal-redux/src/state/shared/network';
import saveToList, * as fromSaveToList from '@web-platform/shared-universal-redux/src/state/shared/saveToList';
import upgradeMessaging, * as fromUpgradeMessaging from '@web-platform/shared-universal-redux/src/state/shared/upgradeMessaging';
import { combineReducers } from 'redux';
import { GLOBAL } from 'saddlebag-browser';

import { getExitingBookingPanelItineraryId } from '../../helpers/getExitingBookingPanelItineraryId';

import combinedComponentKeys, * as fromCombinedComponentKeys from './combinedComponentKeys';
import michelin, * as fromMichelin from './michelin';
import radar, * as fromRadar from './radar';
import searchControls, * as fromSearchControls from './searchControls';
import yolo, * as fromYolo from './yolo';

import type {
  SkyscannerMichelinSearchV1Carhireindicativepricesection,
  SkyscannerMichelinSearchV1Hotelindicativepricesection,
} from '@skyscanner-internal/michelin-web-http-client';
import type { CarHireState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/carHire';
import type { CombinedComponentKeyState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/combinedComponentKeys';
import type { PollingSessionState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/michelin/michelin';
import type { RadarState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/radar';
import type { SearchControlsState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/searchControls';
import type { SharedState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/shared';
import type { PackagesRealTimeSavingsState } from '@web-platform/shared-universal-types/src/redux/state/shared/michelin/packagesRealTimeSavings';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type HotelIndicativePricesSection =
  SkyscannerMichelinSearchV1Hotelindicativepricesection.HotelIndicativePricesSection;
type CarHireIndicativePricesSection =
  SkyscannerMichelinSearchV1Carhireindicativepricesection.CarHireIndicativePricesSection;

const isTokenPanel = (): boolean => {
  const $window = GLOBAL.getWindow();
  return !!($window && matchHashPath($window?.location?.hash ?? '', ITINERARY_PANEL));
};

const flightBookingPanel = fromFlightBookingPanel.createFlightBookingPanelReducer(
  isTokenPanel,
  getExitingBookingPanelItineraryId,
);

const fps = fromFps.createFpsReducer(getExitingBookingPanelItineraryId);

export default combineReducers({
  adverts,
  combinedComponentKeys,
  flightBookingPanel,
  flightSearchResults,
  fps,
  michelin,
  network,
  radar,
  saveToList,
  searchControls,
  upgradeMessaging,
  yolo,
});

// combinedComponentKeys
export const getCombinedComponentKeys = (state: SharedState): CombinedComponentKeyState[] =>
  fromCombinedComponentKeys.getCombinedComponentKeys(state.combinedComponentKeys);
export const isLoadingCombinedComponentKeys = (state: SharedState): boolean =>
  fromCombinedComponentKeys.isLoadingCombinedComponentKeys(state.combinedComponentKeys);

// radar
export const getRadarState = (state: SharedState): RadarState => state.radar;
export const getLastFlightsItinerariesRendered = (state: SharedState): boolean =>
  fromFps.getLastFlightsItinerariesRendered(state.fps);
export const isLoadingRadarMutation = (state: SharedState): boolean =>
  fromRadar.isLoadingRadarMutation(state.radar);

// yolo
export const isReadyToDisplay = (state: SharedState): boolean =>
  fromYolo.isReadyToDisplay(state.yolo);
export const isCookieBannerClosed = (state: SharedState): boolean =>
  fromYolo.isCookieBannerClosed(state.yolo);

// searchControls
export const getSearchControlsState = (state: SharedState): SearchControlsState =>
  fromSearchControls.get(state.searchControls);
export const showSearchControls = (state: SharedState): boolean =>
  fromSearchControls.showSearchControls(state.searchControls);
export const getUserRequestedSearch = (state: SharedState): boolean =>
  fromSearchControls.getUserRequestedSearch(state.searchControls);

// saveToList
export const getS2lNotification = (state: SharedState) =>
  fromSaveToList.getS2lNotification(state.saveToList);
export const getLastUpdatedActionId = (state: SharedState): Nullable<string> =>
  fromSaveToList.getLastUpdatedActionId(state.saveToList);
export const getStatusText = (state: SharedState): Nullable<string> =>
  fromSaveToList.getStatusText(state.saveToList);
export const getErrorDialogText = (state: SharedState) =>
  fromSaveToList.getErrorDialogText(state.saveToList);

// michelin
export const getMichelinPollingState = (state: SharedState): PollingSessionState =>
  fromMichelin.getPollingState(state.michelin);
export const getMichelinCarHire = (state: SharedState): CarHireState =>
  fromMichelin.getCarHire(state.michelin);
export const getMichelinHotels = (state: SharedState) =>
  fromMichelin.getMichelinHotels(state.michelin);
export const getMichelinVerticals = (state: SharedState) =>
  fromMichelin.getVerticals(state.michelin);
export const getHotelIndicativePricing = (state: SharedState): HotelIndicativePricesSection =>
  fromMichelin.getHotelIndicativePricing(state.michelin);
export const getCarHireIndicativePricing = (state: SharedState): CarHireIndicativePricesSection =>
  fromMichelin.getCarHireIndicativePricing(state.michelin);
export const getExploreAccommodations = (state: SharedState) =>
  fromMichelin.getExploreAccommodations(state.michelin);
export const getPackagesRealTimeSavings = (state: SharedState): PackagesRealTimeSavingsState =>
  fromMichelin.getPackagesRealTimeSavings(state.michelin);

// fps
export const hadCompleteFpsResponse = (state: SharedState): boolean =>
  fromFps.getHadCompleteResponse(state.fps);

// upgradeMessaging
export const shouldInjectUpgradeMessaging = (state: SharedState): boolean =>
  fromUpgradeMessaging.get(state.upgradeMessaging);

// flightSearchResult - sort
export const getSelectedSort = (state: SharedState) =>
  fromFlightSearchResults.getSelectedSort(state.flightSearchResults);

// adverts
export const getSponsoredAgent = (state: SharedState) =>
  fromAdverts.getSponsoredAgent(state.adverts);
export const getSponsoredAirlineV2 = (state: SharedState) =>
  fromAdverts.getSponsoredAirlineV2(state.adverts);
export const isSponsoredAirlineV2FilterActive = (state: SharedState) =>
  fromAdverts.isSponsoredAirlineV2FilterActive(state.adverts);

// sponsoredContent
export const allSponsoredContent = (state: SharedState) =>
  fromAdverts.getSponsoredContent(state.adverts);

// flightSearchResults - entities - legs
export const flightLeg = (state: SharedState, id: string) =>
  fromFlightSearchResults.flightLeg(state.flightSearchResults, id);
export const flightAllLegs = (state: SharedState) =>
  fromFlightSearchResults.flightAllLegs(state.flightSearchResults);

// flightSearchResults - entities - segments
export const flightSegment = (state: SharedState, id: string) =>
  fromFlightSearchResults.flightSegment(state.flightSearchResults, id);
export const flightAllSegments = (state: SharedState) =>
  fromFlightSearchResults.flightAllSegments(state.flightSearchResults);

// flightSearchResults - entities - itineraries
export const flightItinerary = (state: SharedState, id: string) =>
  fromFlightSearchResults.flightItinerary(state.flightSearchResults, id);
export const flightAllItineraries = (state: SharedState) =>
  fromFlightSearchResults.flightAllItineraries(state.flightSearchResults);
export const getItineraryFilterStatusMap = (state: SharedState, id: string) =>
  fromFlightSearchResults.getItineraryFilterStatusMap(state.flightSearchResults, id);

// flightSearchResults - entities - alliances
export const flightAlliance = (state: SharedState, id: number) =>
  fromFlightSearchResults.flightAlliance(state.flightSearchResults, id);
export const flightAllAlliances = (state: SharedState) =>
  fromFlightSearchResults.flightAllAlliances(state.flightSearchResults);

// flightBookingPanel - legs
export const flightLegBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.flightLegBookingPanel(state.flightBookingPanel, id);
export const flightAllLegsBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.flightAllLegsBookingPanel(state.flightBookingPanel);

// flightBookingPanel - carriers
export const flightCarrierBookingPanel = (state: SharedState, id: number) =>
  fromFlightBookingPanel.flightCarrierBookingPanel(state.flightBookingPanel, id);
export const flightAllCarriersBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.flightAllCarriersBookingPanel(state.flightBookingPanel);

// flightBookingPanel - segments
export const flightSegmentBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.flightSegmentBookingPanel(state.flightBookingPanel, id);
export const flightAllSegmentsBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.flightAllSegmentsBookingPanel(state.flightBookingPanel);

// flightBookingPanel - agents
export const agentBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.agentBookingPanel(state.flightBookingPanel, id);
export const allAgentsBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.allAgentsBookingPanel(state.flightBookingPanel);

// flightBookingPanel - partnerMetadata
export const getPartnerMetadataBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.getPartnerMetadataBookingPanel(state.flightBookingPanel, id);
export const getAllPartnerMetadataBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.getAllPartnerMetadataBookingPanel(state.flightBookingPanel);

// flightBookingPanel - itineraries
export const flightItineraryBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.flightItineraryBookingPanel(state.flightBookingPanel, id);
export const flightAllItinerariesBookingPanel = (state: SharedState) =>
  fromFlightBookingPanel.flightAllItinerariesBookingPanel(state.flightBookingPanel);
export const getItineraryFilterStatusMapBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.getItineraryFilterStatusMapBookingPanel(state.flightBookingPanel, id);
export const getItineraryErrorBookingPanel = (state: SharedState, id: string) =>
  fromFlightBookingPanel.getItineraryErrorBookingPanel(state.flightBookingPanel, id);
